import React, { createContext, type ReactNode, useContext, useMemo } from "react"
import type { MessageSendStore } from "./messageSendStore"
import type { MessagePickStore } from "./messagePickStore"
import type { MessageFailureStore } from "./messageFailureStore"
import type {
  ProviderAttachmentKind,
  ProviderOutgoingAttachment,
  ProviderThreadList,
  ProviderThreadMessages
} from "../providerMessagingTypes"

/**
 * The PROVIDER (Healthie) messaging transport, reached through the host.
 *
 * ERROR SHAPE: every method's rejections must carry a numeric `statusCode` (`ProviderApiError`),
 * which is what the retry switches in `hooks/useProviderThreads.ts` and `hooks/useThreadMessages.ts`
 * read. Mapping the transport's own errors into that shape is the HOST adapter's job. That is what
 * keeps the package transport-agnostic: nothing here imports the BFF client or knows that tRPC
 * exists.
 */
export type MessagesProviderApi = {
  /**
   * The member's provider conversations — ALL of them, open and closed. NO PARAMETERS: the transport
   * answers the whole list, and there is no "load more" control anywhere in this feature for a page
   * boundary to serve.
   *
   * Answers with `{ threads, hasProviderIdentity }` rather than the array itself. The flag is a
   * property of the MEMBER, not of the list, so an empty `threads` alone cannot express it: see
   * `ProviderThreadList`. A host whose transport genuinely cannot tell should report `true` — the
   * conservative answer, since it claims nothing about what the member does or does not have.
   */
  getThreads: () => Promise<ProviderThreadList>
  /** One thread's whole history, oldest-first. Unpaged, for the same reason `getThreads` is. */
  getThreadMessages: (params: { threadId: string }) => Promise<ProviderThreadMessages>
  /**
   * `clientMessageId` is the caller's idempotency key for the send.
   *
   * `body` is REQUIRED even for a file with no text, which then carries `""` — the transport's
   * blankness rule is "text, a file, or both", so making it optional would be a type change where
   * only a validation changed.
   */
  sendMessage: (params: {
    threadId: string
    body: string
    clientMessageId: string
    /**
     * ONE file to send with the message (CONAPP-367), absent on the ordinary send.
     *
     * ⚠ NOTHING IN THIS PACKAGE DECIDES WHETHER A FILE MAY BE SENT. The type allowlist, the size cap
     * and the bytes-match-the-declared-type check are all the transport's, server-side; a composer
     * that checks the same things is telling the member early, not deciding. A host whose transport
     * has the feature turned off REJECTS this field rather than dropping it, so an attachment that
     * cannot be sent surfaces as a `statusCode`-bearing rejection the composer can branch on —
     * never as a message that silently loses its file.
     */
    attachment?: ProviderOutgoingAttachment
  }) => Promise<{ messageId: string; createdAt: number }>
  markRead: (params: { threadId: string }) => Promise<void>
  /**
   * Where a member's click on one of a message's attachments goes.
   *
   * ⚠ A URL, NOT A CALL, and the only member of this contract that is not a promise. That is the
   * point of it: Healthie signs every attachment url with a ten-second expiry, so the file cannot be
   * fetched ahead of the click and handed over — the href has to be a STABLE url that mints one when
   * followed, and following it is a navigation the browser performs, not a request this package
   * makes. (Fetching it here would also mean holding a bearer credential for a patient's file in
   * front-end memory.)
   *
   * It is on the HOST contract rather than built in this package for the same reason the transport
   * is: the BFF's path is the host's to know. The consumer app answers `/bff/messages/attachment/…`.
   */
  attachmentUrl: (params: { messageId: string; kind: ProviderAttachmentKind }) => string
}

// The HOST contract handed into the provider messages panel.
//
// It exists because everything this feature needs from the app is unreachable from a workspace
// package: the BFF client is host-only, and so is Sentry. The `#/*` alias is a root-tsconfig path, so
// no `packages/*` file can import any of it.
//
// The split is deliberate: the host supplies TRANSPORT, error reporting and its own runtime config,
// and the package owns the react-query layer. Cache keys, `staleTime` and the retry switches live in
// the package, once each.
//
// ⚠ AND FOUR THINGS THE HOST SUPPLIES FOR A DIFFERENT REASON: `draftsByThread`, `sends`, `picks` and
// `failures` are all perfectly reachable from here, and are on the contract because of their
// LIFETIME rather than their reach — see their own notes.
//
// NINE KEYS, where this used to carry the whole Carrot messaging surface as well — the second half
// of a page that no longer lives here. Provider messaging is now the package's only subject: the
// Carrot side of /messages is the app's own (src/components/messaging/**), framed by the app's own
// shell (src/pages/messages-shell.tsx), and the tab bar there is what puts the two beside each other.
// Nothing in this package renders host chrome any more, so `components` is gone with it.
//
// Mirrors the pattern in packages/consumer/src/subscription/context/SubscriptionHostContext.tsx.
export type MessagesHostContract = {
  // REQUIRED, unlike its predecessor. It used to be optional because the package served a host that
  // might have no provider transport at all; the panel it serves now IS the provider transport's UI,
  // so a host without one has nothing to mount. `useProviderUnreadCount` still takes its `getThreads`
  // as an ordinary parameter that may be absent — that is the nav dot deciding not to ask, which is a
  // different thing from a host that cannot answer.
  providerApi: MessagesProviderApi
  // The host's Sentry wrappers (src/utils/ErrorReporting.ts).
  reportError: (error: unknown, context?: Record<string, string>) => void
  reportErrorMessage: (message: string) => void
  /**
   * Every conversation's unsent words, keyed by thread id (CONAPP-528).
   *
   * ⛔ THE HOST OWNS IT BECAUSE THE PANEL IS NOT LONG-LIVED ENOUGH TO. Two unmounts destroy a store
   * held below this line: `ProviderMessagesPanel` swaps `MessageReplyContainer` for the closed notice
   * on a conversation it cannot reply to, and the app's tab bar unmounts the WHOLE PANEL when the
   * member looks at their Carrot Companion conversations. The second one takes any store inside the
   * panel with it, drafts and all — measured on stage, and the reason this key exists.
   *
   * ⛔ REQUIRED, so a host cannot answer the lifetime question by omission. Optional-and-absent would
   * mean a package-owned fallback that reads correctly and dies at the first tab switch, which is
   * exactly the defect. ⚠ IT MUST NOT BE A MODULE CONSTANT either: drafts are a signed-in member's
   * clinical words, so the store has to belong to a component instance that unmounts with the page
   * (see `MessagesShell`) rather than to the tab.
   *
   * ⚠ A DICTIONARY, NOT ONE SLOT. A pick is displaced by the member choosing another file; a draft is
   * displaced by MOVING, which is no decision about the words at all — so two conversations left
   * mid-sentence need two entries, or returning to the first meets a sentence silently thrown away.
   *
   * ⚠ A MUTABLE MAP AND NOT STATE: nothing renders from it. The composer reads it on the way into a
   * conversation and writes it on the way out, so state here would only add renders that change
   * nothing on screen — and re-rendering the whole panel on every keystroke is what that would be.
   */
  draftsByThread: Map<string, string>
  /**
   * Which conversations have a send still out, and which have had one POST (CONAPP-530).
   *
   * ⛔ THE HOST OWNS IT FOR THE SAME REASON IT OWNS THE DRAFTS ABOVE, and the cost of getting it
   * wrong is worse. A send survives both of the unmounts described there, but the guard that refuses
   * a second one does not: the composer that comes back has a fresh guard, a fresh `sending` and the
   * submitted body handed straight back to it by `DraftSync` — so Send is live for a note that is
   * still in flight, and one tap posts it twice. `createNote` has no idempotency key and a note
   * attributed to the member cannot be deleted with our credential, so the duplicate is permanent.
   *
   * ⛔ REQUIRED, and ⛔ NOT A MODULE CONSTANT, on the same terms as `draftsByThread`. An optional key
   * with a package-side fallback reads correctly and dies at the first remount, which is the defect.
   *
   * ⚠ A STORE AND NOT A MAP, which is where it differs from the drafts: the composer RENDERS from
   * this one. The send that has to give the controls back finishes in a closure belonging to an
   * unmounted composer, so the live one has to be told — see `MessageSendStore`.
   */
  sends: MessageSendStore
  /**
   * What each conversation has attached, is still compressing, or was last refused (CONAPP-566).
   *
   * ⛔ THE HOST OWNS IT FOR THE SAME REASON IT OWNS THE TWO ABOVE, and here the unmounts are THREE:
   * the closed-conversation swap, the Companion tab, and the list poll exhausting its retries — that
   * last one on a phone with no member action at all. The words come back from `draftsByThread` and
   * the pick did not, so the member met their own sentence, no chip and a live Send, and one tap
   * posted a note referencing a file that was never sent.
   *
   * ⛔ REQUIRED, and ⛔ NOT A MODULE CONSTANT, on the same terms as the two above: a filename is
   * member-supplied clinical content.
   *
   * ⚠ A STORE AND NOT A MAP, for the reason `sends` is one: the chip, the pending row and the send
   * gate all render from it, and a compression finishes inside an unmounted composer's closure — see
   * `MessagePickStore`, which also records why all five pieces of pick state had to move together.
   */
  picks: MessagePickStore
  /**
   * Which conversation's last reply failed to send, and what refused it (CONAPP-559).
   *
   * ⛔ THE HOST OWNS IT FOR THE SAME REASON IT OWNS THE THREE ABOVE, and here the thing that used to
   * be lost is the TELLING. Held in the panel's own context, a failure was cleared the moment the
   * member selected another conversation — so a send that broke while they were reading elsewhere
   * was never reported at all — and a panel remount left the composer writing its verdict into a
   * provider that no longer existed.
   *
   * ⛔ WORST ON A 400. The composer has already removed the chip by then, so the member came back to
   * their restored words with no chip, no notice and no banner, and the next tap posted the note
   * without the file — permanently, since `createNote` has no idempotency key.
   *
   * ⛔ REQUIRED, and ⛔ NOT A MODULE CONSTANT, on the same terms as the three above.
   *
   * ⚠ A STORE AND NOT A MAP, for the reason `sends` and `picks` are: the transcript's banner renders
   * from it, and the send it describes settles inside an unmounted composer's closure — see
   * `MessageFailureStore`, which also records the one rule that still clears a banner.
   */
  failures: MessageFailureStore
  /**
   * Whether this deployment has message attachments turned on (CONAPP-367) — the CLIENT half of the
   * gate, and the composer's only input to whether it renders an attach control at all.
   *
   * ⛔ OPTIONAL, AND ABSENT IS OFF. It fails closed on purpose: a host that has never heard of the
   * feature must not acquire it by upgrading the package.
   *
   * ⚠ NOT A SECOND SOURCE OF TRUTH, and hiding the button decides nothing on its own. The BFF
   * refuses a send carrying a file unless its OWN `HEALTHIE_MESSAGE_ATTACHMENTS_ENABLED` is "true"
   * (routers/messages.ts), which is what makes this a gate rather than a decoration — the procedure
   * is reachable without the composer. Both halves exist deliberately; a deployment that turns one
   * on and not the other gets a member who can pick a file and a transport that refuses it, which
   * the composer surfaces as a refusal rather than as a lost file.
   *
   * A BOOLEAN, resolved by the HOST from its own runtime config (`window.env` via
   * `CarrotConfig.ts`). Nothing in this package reads host config directly — the same rule the
   * consumer package's `stripePublishableKey` follows, and here it is also a hard constraint:
   * `window.env`'s type augmentation is a host-side declaration no `packages/*` file can see.
   */
  attachmentsEnabled?: boolean
  /**
   * Whether this deployment accepts a JPEG or PNG up to `MAX_MESSAGE_IMAGE_SOURCE_BYTES` and
   * compresses it down to the wire cap (CONAPP-516), or keeps images on the PDF's flat cap.
   *
   * ⛔ ABSENT IS OFF, and a host-resolved boolean, for the same reasons `attachmentsEnabled` above is
   * both. ⚠ NOT IN CONFLICT WITH SHIPPING IT ON — on is a value in the consumer app's `config/*.js`,
   * not a default here.
   *
   * ⚠ A SIBLING OF `attachmentsEnabled` RATHER THAN A FINER SETTING OF IT, because that key gates the
   * whole attach control and is already on wherever attachments ship: backing this change out through
   * it would mean taking attachments away from every member. What it insures against is device memory
   * while compressing a 25 MiB source on a low-end Android, which CI cannot reproduce.
   *
   * ⚠ IT SELECTS THE COPY AS WELL AS THE BEHAVIOUR: off, the refusal names the wire cap, never the
   * source bound — a member told to choose an image "25 MB or smaller" and then refused at 4 MB has
   * been lied to.
   */
  largeImageAttachmentsEnabled?: boolean
}

const MessagesHostContext = createContext<MessagesHostContract | null>(null)

export function useMessagesHost(): MessagesHostContract {
  const ctx = useContext(MessagesHostContext)
  if (ctx === null) {
    throw new Error("useMessagesHost must be used within <MessagesHostProvider>")
  }
  return ctx
}

export function MessagesHostProvider({
  providerApi,
  reportError,
  reportErrorMessage,
  draftsByThread,
  sends,
  picks,
  failures,
  attachmentsEnabled,
  largeImageAttachmentsEnabled,
  children
}: MessagesHostContract & { children: ReactNode }): JSX.Element {
  const value = useMemo<MessagesHostContract>(
    () => ({
      providerApi,
      reportError,
      reportErrorMessage,
      draftsByThread,
      sends,
      picks,
      failures,
      attachmentsEnabled,
      largeImageAttachmentsEnabled
    }),
    [
      providerApi,
      reportError,
      reportErrorMessage,
      draftsByThread,
      sends,
      picks,
      failures,
      attachmentsEnabled,
      largeImageAttachmentsEnabled
    ]
  )
  return <MessagesHostContext.Provider value={value}>{children}</MessagesHostContext.Provider>
}
